import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PasswordChecklist from "react-password-checklist";
import { ApiHook } from "../../hooks/apiHook";
import { passwordRules } from "../../Validator/register";
import logo_user from "../../assests/images/iCareNet logo_page-0001.jpg";
import "./securityReset.css";

/**
 * صفحة تعيين كلمتي المرور بعد التسجيل (onboarding).
 * مستقلة تماماً عن صفحة إعادة التعيين الأمنية أو استعادة الحساب.
 *
 * قسمان مستقلان: كلمة مرور الحساب، وكلمة مرور المحفظة. كل قسم يُرسل على حدة،
 * ويمكن للمستخدم العودة لاحقاً بنفس الرابط لإكمال القسم الآخر قبل انتهاء المدة.
 */
const EyeIcon = ({ open }) => (
  open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
);

export const SetPasswordsForm = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = params.token;

  ApiHook.CallCompanyLogo();
  const companyDetails = useSelector((state) => state.replica?.companyDetails);
  const brandLogo = companyDetails?.logo || logo_user;
  const brandName = companyDetails?.name || "iCare";

  const verifyMutation = ApiHook.CallOnboardingVerify();
  const accountMutation = ApiHook.CallOnboardingAccountPassword();
  const walletMutation = ApiHook.CallOnboardingWalletPassword();

  const [state, setState] = useState({
    loading: true,
    valid: false,
    username: "",
    passwordPolicy: null,
    accountPending: false,
    walletPending: false,
  });

  const [accountPassword, setAccountPassword] = useState("");
  const [accountConfirm, setAccountConfirm] = useState("");
  const [accountError, setAccountError] = useState("");
  const [accountShow, setAccountShow] = useState(false);

  const [walletPassword, setWalletPassword] = useState("");
  const [walletConfirm, setWalletConfirm] = useState("");
  const [walletError, setWalletError] = useState("");
  const [walletShow, setWalletShow] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await verifyMutation.mutateAsync({ token });
        if (!active) return;
        if (res?.status) {
          const d = res.data || {};
          setState({
            loading: false,
            valid: true,
            username: d.username || "",
            passwordPolicy: d.passwordPolicy || null,
            accountPending: !!d.accountPending,
            walletPending: !!d.walletPending,
          });
        } else {
          setState((s) => ({ ...s, loading: false, valid: false }));
          toast.error(res?.data?.description || t("invalid_or_expired_link", "Invalid or expired link"));
          setTimeout(() => navigate("/login"), 3000);
        }
      } catch (e) {
        if (!active) return;
        setState((s) => ({ ...s, loading: false, valid: false }));
        toast.error(t("invalid_or_expired_link", "Invalid or expired link"));
        setTimeout(() => navigate("/login"), 3000);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const checklistRules = state.passwordPolicy ? [...passwordRules(state.passwordPolicy), "match"] : ["match"];

  const submitAccount = async () => {
    setAccountError("");
    if (accountPassword !== accountConfirm) {
      setAccountError(t("passwords_do_not_match", "Passwords do not match"));
      return;
    }
    if (!accountPassword) {
      setAccountError(t("this_field_is_required", "This field is required"));
      return;
    }
    try {
      const res = await accountMutation.mutateAsync({
        token,
        password: accountPassword,
        passwordConfirm: accountConfirm,
      });
      if (res?.status) {
        toast.success(t("account_password_updated", "Account password updated successfully"));
        setState((s) => ({ ...s, accountPending: false, walletPending: !!res.data?.walletPending }));
        setAccountPassword("");
        setAccountConfirm("");
      } else {
        setAccountError(res?.data?.description || t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
      }
    } catch (e) {
      setAccountError(t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
    }
  };

  const submitWallet = async () => {
    setWalletError("");
    if (walletPassword !== walletConfirm) {
      setWalletError(t("passwords_do_not_match", "Passwords do not match"));
      return;
    }
    if (!walletPassword) {
      setWalletError(t("this_field_is_required", "This field is required"));
      return;
    }
    try {
      const res = await walletMutation.mutateAsync({
        token,
        password: walletPassword,
        passwordConfirm: walletConfirm,
      });
      if (res?.status) {
        toast.success(t("wallet_password_updated", "Wallet password updated successfully"));
        setState((s) => ({ ...s, walletPending: false, accountPending: !!res.data?.accountPending }));
        setWalletPassword("");
        setWalletConfirm("");
      } else {
        setWalletError(res?.data?.description || t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
      }
    } catch (e) {
      setWalletError(t("errorOccuredPleaseTryAgain", "An error occurred, please try again"));
    }
  };

  if (state.loading) {
    return (
      <section className="loginSection sr-section">
        <div className="container centerDiv">
          <div className="sr-card sr-card--center">
            <div className="sr-spinner" aria-hidden="true"></div>
            <p className="sr-muted">{t("please_wait", "Please wait...")}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!state.valid) return null;

  const allDone = !state.accountPending && !state.walletPending;

  const StatusChip = ({ label, pending }) => (
    <span className={`sr-chip ${pending ? "sr-chip--pending" : "sr-chip--done"}`}>
      <span className="sr-chip__dot" aria-hidden="true"></span>
      {label}: {pending ? t("sr_pending", "Pending") : t("sr_done", "Done")}
    </span>
  );

  return (
    <section className="loginSection sr-section">
      <div className="container centerDiv">
        <div className="sr-card">
          <div className="sr-header">
            <div className="sr-logo">
              <img src={brandLogo} alt={brandName} />
            </div>
            <h4 className="sr-title">
              {t("onboarding_title", "Welcome! Set your passwords")}
            </h4>
            <p className="sr-muted">
              {t(
                "onboarding_intro",
                "Your account has been created. Please set your account password and transaction password below to get started."
              )}
            </p>
            {state.username && (
              <div className="sr-user">
                <span className="sr-user__label">{t("username", "Username")}</span>
                <span className="sr-user__value">{state.username}</span>
              </div>
            )}
            <div className="sr-steps">
              <StatusChip label={t("account_password", "Account password")} pending={state.accountPending} />
              <StatusChip label={t("wallet_password", "Wallet (transaction) password")} pending={state.walletPending} />
            </div>
          </div>

          {allDone && (
            <div className="sr-done">
              <div className="sr-done__icon" aria-hidden="true">
                <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <p>
                {t(
                  "onboarding_all_done",
                  "All passwords have been set. You can now sign in to your account."
                )}
              </p>
              <button type="button" className="sr-btn" onClick={() => navigate("/login")}>
                {t("Login", "Login")}
              </button>
            </div>
          )}

          {/* ---------- قسم كلمة مرور الحساب ---------- */}
          {state.accountPending && (
            <div className="sr-panel">
              <div className="sr-panel__head">
                <span className="sr-panel__index">1</span>
                <h5>{t("account_password", "Account password")}</h5>
              </div>
              <div className="sr-field">
                <label htmlFor="accountPassword">{t("newPassword", "New password")}</label>
                <div className="sr-input">
                  <input
                    type={accountShow ? "text" : "password"}
                    id="accountPassword"
                    name="accountPassword"
                    placeholder={t("newPassword", "New password")}
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="sr-eye"
                    onClick={() => setAccountShow((v) => !v)}
                    aria-label={accountShow ? t("hide", "Hide") : t("show", "Show")}
                  >
                    <EyeIcon open={accountShow} />
                  </button>
                </div>
              </div>
              <div className="sr-field">
                <label htmlFor="accountConfirm">{t("confirm_password", "Confirm password")}</label>
                <div className="sr-input">
                  <input
                    type={accountShow ? "text" : "password"}
                    id="accountConfirm"
                    name="accountConfirm"
                    placeholder={t("confirm_password", "Confirm password")}
                    value={accountConfirm}
                    onChange={(e) => setAccountConfirm(e.target.value)}
                  />
                </div>
                <div className="sr-checklist">
                  <PasswordChecklist
                    rules={checklistRules}
                    minLength={state.passwordPolicy?.minLength}
                    value={accountPassword}
                    valueAgain={accountConfirm}
                  />
                </div>
              </div>
              {accountError && <p className="sr-error">{accountError}</p>}
              <button
                type="button"
                className="sr-btn"
                disabled={accountMutation.isLoading}
                onClick={submitAccount}
              >
                {accountMutation.isLoading ? t("please_wait", "Please wait...") : t("set_account_password", "Set account password")}
              </button>
            </div>
          )}

          {/* ---------- قسم كلمة مرور المحفظة ---------- */}
          {state.walletPending && (
            <div className="sr-panel">
              <div className="sr-panel__head">
                <span className="sr-panel__index">{state.accountPending ? 2 : 1}</span>
                <h5>{t("wallet_password", "Wallet (transaction) password")}</h5>
              </div>
              <div className="sr-field">
                <label htmlFor="walletPassword">{t("newPassword", "New password")}</label>
                <div className="sr-input">
                  <input
                    type={walletShow ? "text" : "password"}
                    id="walletPassword"
                    name="walletPassword"
                    placeholder={t("newPassword", "New password")}
                    value={walletPassword}
                    onChange={(e) => setWalletPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="sr-eye"
                    onClick={() => setWalletShow((v) => !v)}
                    aria-label={walletShow ? t("hide", "Hide") : t("show", "Show")}
                  >
                    <EyeIcon open={walletShow} />
                  </button>
                </div>
              </div>
              <div className="sr-field">
                <label htmlFor="walletConfirm">{t("confirm_password", "Confirm password")}</label>
                <div className="sr-input">
                  <input
                    type={walletShow ? "text" : "password"}
                    id="walletConfirm"
                    name="walletConfirm"
                    placeholder={t("confirm_password", "Confirm password")}
                    value={walletConfirm}
                    onChange={(e) => setWalletConfirm(e.target.value)}
                  />
                </div>
                <div className="sr-checklist">
                  <PasswordChecklist
                    rules={checklistRules}
                    minLength={state.passwordPolicy?.minLength}
                    value={walletPassword}
                    valueAgain={walletConfirm}
                  />
                </div>
              </div>
              {walletError && <p className="sr-error">{walletError}</p>}
              <button
                type="button"
                className="sr-btn"
                disabled={walletMutation.isLoading}
                onClick={submitWallet}
              >
                {walletMutation.isLoading ? t("please_wait", "Please wait...") : t("set_wallet_password", "Set wallet password")}
              </button>
            </div>
          )}

          <p className="sr-footnote">{brandName}</p>
        </div>
      </div>
    </section>
  );
};

export default SetPasswordsForm;
